<template>
  <div
    class="loi-container"
    @click
  >
    <div class="loi-header">
      <span class="u-tt-cap">{{ formAction }}</span> Location of Interest
    </div>
    <div class="fields-container">
      <div class="form-error">
        {{ formErrors.generic }}
      </div>
      <label for="url">
        <span
          class="required-asterisk"
          title="Field is required."
        >*</span>
        URL
        <span class="form-error">{{ formErrors.url }}</span>
      </label>
      <input
        ref="url"
        v-model="url"
        type="url"
        required
      >
      <label for="title">
        <span
          class="required-asterisk"
          title="Field is required."
        >*</span>
        Title
        <span class="form-error">{{ formErrors.title }}</span>
      </label>
      <autocomplete-input
        :initialValue="initialData ? initialData.title : ''"
        :suggestions="titleSiteNames"
        @getSuggestions="getSiteNamesForTitle"
        @setInput="updateTitle"
      />
      <label
        for="site-name"
        class="site-name-label"
      >
        <span
          class="required-asterisk"
          title="Field is required."
        >*</span>
        <span title="Links the links of interest to a site">Site Name</span>
        <span>
          <input
            id="same-as-title"
            class="same-as-title"
            type="checkbox"
            v-model="siteNameSameAsTitle"
            @change="updateSiteName"
          />
          <label for="same-as-title" class="same-as-title-label">same as title</label>
        </span>
        <span class="form-error">{{ formErrors.site_name }}</span>
      </label>
      <input
        v-if="siteNameSameAsTitle"
        id="site-name"
        v-model="siteName"
        type="text"
        disabled
      />
      <autocomplete-input
        v-else
        :initialValue="initialData ? initialData.site_name : ''"
        :suggestions="siteNames"
        @getSuggestions="getSiteNamesForSiteName"
        @setInput="(newSiteName) => siteName = newSiteName"
      />

      <label for="notes">Notes</label>
      <textarea
        v-model="notes"
        rows="3"
      />
      <div class="site-crawl-info">
        <div class="site-crawl-info-field">
          <label for="site_status">Site Status</label>
          <select v-model="siteStatus">
            <option value="U">
              Up
            </option>
            <option value="D">
              Down
            </option>
          </select>
        </div>
        <div class="site-crawl-info-field">
          <label for="has_scraped">Site Crawled</label>
          <select v-model="hasScraped">
            <option value="true">
              Yes
            </option>
            <option value="false">
              No
            </option>
          </select>
        </div>
      </div>
      <label for="tags">Tags</label>
      <div class="tags-container">
        <topic-links-tag
          :tags="tags"
          :fullTagList="fullTagList"
          @addNewTag="(newTags) => tags = newTags"
        />
      </div>
      <label for="site-users">Site Users</label>
      <div class="site-user-table-container">
        <site-user-table
          :initialUsers="initialData ? initialData.site_users : []"
          @siteUsersChange="newSiteUsers => siteUsers = newSiteUsers"
        />
      </div>
    </div>
    <div class="buttons-container">
      <button
        class="btn btn--cancel"
        type="button"
        @click.prevent="$emit('formCancel')"
      >
        Cancel
      </button>
      <button
        class="btn--primary btn"
        type="button"
        :disabled="!url || !title"
        @click="submitForm"
      >
        <span class="u-tt-cap">{{ formAction }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import AutocompleteInput from '../AutocompleteInput'
import axios from 'axios'
import Cookies from 'js-cookie'
import SiteUserTable from './SiteUserTable'
import TopicLinksTag from '../TopicLinksTag'

export default {
  name: 'LocationOfInterestForm',
  components: {
    AutocompleteInput,
    SiteUserTable,
    TopicLinksTag,
  },
  props: {
    existingUrls: {
      type: Array,
      default: () => []
    },
    formAction: {
      type: String,
      default: 'create'
    },
    fullTagList: {
      type: Array,
      default: () => []
    },
    initialData: {
      type: Object,
      default: () => {}
    },
  },
  emits: [
    'formCancel',
    'formSubmit'
  ],
  data() {
    return {
      formErrors: {
        generic: '',
        url: '',
      },
      hasScraped: 'false',
      notes: '',
      siteNames: [],
      titleSiteNames: [],
      siteStatus: 'U',
      siteUsers: [],
      submissionUrl: '/en/topic/add/',
      tags: [],
      title: '',
      url: '',
      siteNameSameAsTitle: true,
      siteName: ''
    }
  },
  mounted() {
    // when conditionally rendered with v-if, this function gets called
    // whenever v-if is set to true.

    if (this.initialData?.has_scraped) { this.hasScraped = String(this.initialData.has_scraped) }
    if (this.initialData?.url) { this.url = this.initialData.url }
    if (this.initialData?.notes) { this.notes = this.initialData.notes }
    if (this.initialData?.site_status_type) { this.siteStatus = this.initialData.site_status_type }
    if (this.initialData?.tags) {
      for (let t of this.initialData.tags) {
        this.tags.push(t)
      }
    }
    if (this.initialData?.site_name) {
      this.siteName = this.initialData.site_name
      this.siteNameSameAsTitle = false
    }
    if (this.initialData?.title) {
      this.title = this.initialData.title
      if (!this.siteName) {
        this.siteName = this.initialData.title
      }
    }
    if (this.initialData?.site_users) {
      this.siteUsers = this.initialData.site_users
    }

    this.$nextTick(() => {
      this.$refs.url.focus()
    })
  },
  methods: {
    formHasErrors() {
      // remove any pre-existing errors before checking again
      this.resetFormErrors()
      // check url validity
      if (!this.$refs.url.checkValidity()) {
        if (this.url == null) { // required url is empty
          this.formErrors.url = `* ${this.$refs.url.validationMessage}`
        } else { // url has bad scheme
          this.formErrors.url = '* Please enter a URL beginning with http:// or https://.'
        }
        this.$refs.url.focus()
        return true
      }
      // check url existance
      if (this.url && this.existingUrls.indexOf(this.url) >= 0) {
        if (this.formAction === 'create' || (this.formAction === 'update' && this.initialData.url !== this.url)) {
          this.formErrors.url = '* This URL already exists as a Location of Interest.'
          this.$refs.url.focus()
          return true
        }
      }
      // all the checks pass and so form is valid
      return false
    },
    getSiteNamesForTitle(term) {
      this.title = term
      if (this.siteNameSameAsTitle) {
        this.siteName = term
      }
      axios.get(`/en/message-search/get_site_names/?query=${term}`)
        .then((response) => {
          this.titleSiteNames = response.data
        })
    },
    getSiteNamesForSiteName(term) {
      this.siteName = term
      axios.get(`/en/message-search/get_site_names/?query=${term}`)
        .then((response) => {
          this.siteNames = response.data
        })
    },
    resetFormErrors() {
      for (const [field] of Object.entries(this.formErrors)) {
        this.formErrors[field] = ''
      }
    },
    submitForm() {
      if (this.formHasErrors()) {
        return
      }

      let formData = {
        'has_scraped': this.hasScraped === 'true',
        'notes': this.notes,
        'site_status': this.siteStatus,
        'site_users': this.siteUsers,
        'tags': this.tags,
        'title': this.title,
        'site_name': this.siteName,
      }

      if (this.formAction === 'create') { // creates don't have a new_url
        formData['url'] = this.url
      } else if (this.formAction === 'update') {
        if (this.initialData.url === this.url) { // url wasn't updated
          formData['url'] = this.url
        } else { // url was updated
          formData['url'] = this.initialData.url
          formData['new_url'] = this.url
        }
      }

      const csrftoken = Cookies.get('csrftoken')
      axios.post('/en/topic/add/', formData, {'headers': {'X-CSRFToken': csrftoken}}).then(response => {
        this.$emit('formSubmit')
      })
    },
    updateSiteName() {
      if (this.siteNameSameAsTitle) {
        this.siteName = this.title
      }
    },
    updateTitle(newTitle) {
      this.title = newTitle
      this.updateSiteName()
    }
  }
}
</script>

<style scoped>
.loi-container {
  width: 50%;
  max-height: 95vh;
  min-height: 65vh;
  margin: auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 2px solid #ccc;
  background: #fff;
  padding: 15px;
  .loi-header {
    font-size: x-large;
    font-weight: bold;
    padding: 0 1.2rem;
    margin-bottom: 0.25rem;
    border-bottom: 2px solid lightgrey;
  }
  .fields-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    flex-grow: 1;
    overflow-y: auto;
    padding: 0 0.6rem;
    label, select {
      padding-left: 0.6rem;
    }
    label {
      margin-top: 0.6rem;
    }
    .required-asterisk {
      position: absolute;
      top: 0.1em;
      left: -0.1em;
      font-style: italic;
      color: #a42334;
      margin-right: 0.05rem;
      &[title] {
        cursor: default;
      }
    }
    textarea {
      resize: none;
      min-height: 4rem;
      height: 4rem;
    }
    .form-error {
      color: #a42334;
      position: absolute;
      right: 0;
      top: 0;
      margin-right: 10px;
    }
  }
  .site-crawl-info {
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 1rem;
    .site-crawl-info-field {
      width: 100%;
    }
  }
  .tags-container {
    margin-bottom: 0;
  }
  .buttons-container {
    margin-top: 0.5rem;
    margin-bottom: 0;
    border-top: 2px solid lightgrey;
    padding: 0.75rem 0.6rem 0.75rem 0;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 15px;
  }
}
.site-name-label {
  position: relative;
}
.same-as-title-label {
  font-style: italic;
  font-size: 0.85em;
  margin-bottom: 0;
  color: black;
}
.site-name-label > span:nth-child(3) {
  position: absolute;
  right: 0;
  top: -10px;
}
.site-name-label > span:nth-child(3) > label:nth-child(2) {
  padding-right: 30px;
}
input[type="checkbox"] + label::before, input[type="checkbox"] + .label::before {
  left: auto;
  right: 0;
}
input[type="checkbox"]:checked + label::after, input[type="checkbox"]:checked + .label::after {
  left: auto;
  right: 0.1em;
}
</style>
